import axios from "axios";
import store from "store";
import { logoutRequest } from "store/state/auth";
import { ToastError } from "components/Global/Toasts/Toasty";
import { getEnvConfig } from "helpers/environment-config";
import { getSessionCookie } from "utils/sessionCookie";

const JSON_MIME_TYPE = "application/json";

/**
 *
 * @param {String} path API path to call. Base set by env variable.
 * @param {String} method HTTP verb for the request
 * @param {Object} body Body to pass
 * @param {*} headers
 */
const apiV2Request = async (path, method = "GET", body = null, headers = {}, withCredentials = false) => {
  const params = {
    method,
    headers: {
      "Content-Type": JSON_MIME_TYPE,
      Accept: JSON_MIME_TYPE,
      ...headers
    },
    withCredentials
  };

  const envConfigUrl = await getEnvConfig("apiV2Url");

  params.url = `${envConfigUrl}${path}`;

  if (body) {
    if (method === "GET") {
      throw new Error("GET requests can not have a body.");
    }
    params.data = body;
  }
  const response = await axios(params);
  
  return response.data;
};

const apiRequest = async (path, method = "GET", body = null, headers = {}, withCredentials = false) => {
  const params = {
    method,
    headers: {
      "Content-Type": JSON_MIME_TYPE,
      Accept: JSON_MIME_TYPE,
      ...headers
    },
    withCredentials
  };

  const envConfigUrl = await getEnvConfig();
  params.url = `${envConfigUrl}/api${path}`;

  if (body) {
    if (method === "GET") {
      throw new Error("GET requests can not have a body.");
    }
    params.data = body;
  }
  const response = await axios(params);
  
  return response.data;
};

export const authorizedRequest = async (path, method = "GET", body = null, headers = null, useV2Api = false, authToken = null) => {
  const storedToken = await getSessionCookie();
  const token = authToken || storedToken;
  const Authorization = `Bearer ${token}`;

  try {
    return !useV2Api ?
      await apiRequest(path, method, body, {
        ...headers,
        Authorization
      }, true) :
      await apiV2Request(path, method, body, {
        ...headers,
        Authorization
      });
  } catch (error) {
    if (error.response?.status === 401) {
      store.dispatch(logoutRequest({ backTo: window.location.pathname + window.location.search }));
    }

    throw error;
  }
};

export const bffRequest = async (url, method = "GET", body = null, headers = {}, withCredentials = false) => {
  const params = {
    url,
    method,
    headers: {
      "Content-Type": JSON_MIME_TYPE,
      Accept: JSON_MIME_TYPE,
      ...headers
    },
    withCredentials
  };

  if (body) {
    if (method === "GET") {
      throw new Error("Invalid request");
    }
    params.data = body;
  }
  const response = await axios(params);

  return response.data;
};

export const authorizedBffRequest = async (url, method = "GET", body = null, headers = null) => {
  const token = await getSessionCookie();
  const Authorization = `Bearer ${token}`;

  try {
    return await bffRequest(url, method, body, { ...headers, Authorization }, true);
  } catch (error) {
    if (error.response?.status === 401) {
      store.dispatch(logoutRequest({ backTo: window.location.pathname + window.location.search }));
    }

    throw error;
  }
};

export const authorizedReportsBffRequest = async (path, method, body, headers) => {
  const reportsBffUrl = await getEnvConfig("reactAppReportsApiUrl");
  const url = `${reportsBffUrl}${path}`;

  return authorizedBffRequest(url, method, body, headers);
};

export const uploadFile = async (payload, method = "POST") => {
  const { type, name, size } = payload.file;
  if (size < 10000000000) {
    const token = await getSessionCookie();
    const Authorization = `Bearer ${token}`;
    const headers = {
      "Content-Type": type,
      "Content-Disposition": `attachment; filename="${name}"`,
      Authorization
    };
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("model", payload.model);
    formData.append("modelId", payload.modelId);

    return apiRequest(payload.url, method, formData, headers);
  }

  throw Error("File exceeds size limit");
};

export const downloadFile = async (url, method = "GET", payload) => {
  const token = await getSessionCookie();
  const Authorization = `Bearer ${token}`;
  const envConfigUrl = await getEnvConfig();
  const params = {
    url: `${envConfigUrl}/api/${url}`,
    method,
    responseType: "blob",
    headers: {
      Authorization
    },
    data: payload
  };
  const {
    data,
    headers: { "content-type": type, "content-filename": filename }
  } = await axios(params);

  if (navigator.appVersion.toString().indexOf(".NET") > 0) {
    window.navigator.msSaveBlob(new Blob([data], { type }), filename);
  } else {
    const url = window.URL.createObjectURL(new Blob([data], { type }));
    const link = document.createElementNS("http://www.w3.org/1999/xhtml", "a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
  }
};

export const downloadFileAsArrayBuffer = async url => {
  const token = await getSessionCookie();
  const baseApiUrl = await getEnvConfig();
  const result = await axios({
    url: `${baseApiUrl}/api/${url}`,
    method: "GET",
    responseType: "blob",
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  const blob = result.data;

  return await blob.arrayBuffer();
};

export const downloadLocalFile = async (
  url,
  httpMethod,
  payload,
  downloadName
) => {
  const token = await getSessionCookie();
  const baseApiUrl = await getEnvConfig();
  const params = {
    url: `${baseApiUrl}${url}`,
    method: httpMethod,
    responseType: "blob",
    headers: {
      Authorization: `Bearer ${token}`
    },
    data: payload
  };

  const {
    data,
    headers: { "content-type": type, "content-filename": filename }
  } = await axios(params);

  if (navigator.appVersion.toString().indexOf(".NET") > 0) {
    window.navigator.msSaveBlob(new Blob([data], { type }), filename);
  } else {
    const url = window.URL.createObjectURL(new Blob([data], { type }));
    const link = document.createElementNS("http://www.w3.org/1999/xhtml", "a");
    link.href = url;
    link.download = downloadName || filename;
    document.body.appendChild(link);
    link.click();
  }
};

export const downloadXLSFile = async (url, httpMethod, payload, downloadName) => {
  const token = await getSessionCookie();
  const Authorization = `Bearer ${token}`;
  const envConfigUrl = await getEnvConfig();
  const params = {
    url: `${envConfigUrl}/api/${url}`,
    method: httpMethod,
    responseType: "blob",
    headers: {
      Authorization
    },
    data: payload
  };

  let results = { status: 0 };
  try {
    results = await axios(params);
  } catch (error) {
    ToastError("Export Failed");
  }

  if (results.status === 204) {
    ToastError("You have submitted no quotes to this deal.");

    return;
  }
  if (results.status === 200) {
    const {
      data,
      headers: { "content-type": type, "content-filename": filename }
    } = results;

    if (navigator.appVersion.toString().indexOf(".NET") > 0) {
      window.navigator.msSaveBlob(new Blob([data], { type }), filename);
    } else {
      const url = window.URL.createObjectURL(new Blob([data], { type }));
      const link = document.createElementNS("http://www.w3.org/1999/xhtml", "a");
      link.href = url;
      link.download = downloadName || filename;
      document.body.appendChild(link);
      link.click();
    }
  }
};

export const getAuthorizedHtmlRequest = async (url, method = "GET", body = null, withCredentials = false) => {
  const token = await getSessionCookie();
  const Authorization = `Bearer ${token}`;
  const params = {
    method,
    headers: {
      "Content-Type": JSON_MIME_TYPE,
      Accept: "text/html",
      Authorization
    },
    withCredentials
  };

  params.url = url;

  if (body) {
    if (method === "GET") {
      throw new Error("GET requests can not have a body.");
    }

    params.data = body;
  }

  const response = await axios(params);

  return response.data;
};

export default apiRequest;
